/**
 * TanStack Query hooks for Landing CMS
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchWithAuth, safeJsonParse, API_URL } from '@/utils/api';

const API_BASE = API_URL;

// ============================================================
// TYPES
// ============================================================

export interface LandingPageContentData {
  id?: string;
  hero?: Record<string, any> | null;
  stats?: Record<string, any> | null;
  features?: Record<string, any> | null;
  howItWorks?: Record<string, any> | null;
  pricing?: Record<string, any> | null;
  testimonials?: Record<string, any> | null;
  team?: Record<string, any> | null;
  cta?: Record<string, any> | null;
  footer?: Record<string, any> | null;
  seo?: Record<string, any> | null;
  sectionOrder?: string[] | null;
  isPublished?: boolean;
  publishedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export type LandingSectionName =
  | 'hero'
  | 'stats'
  | 'features'
  | 'howItWorks'
  | 'pricing'
  | 'testimonials'
  | 'team'
  | 'cta'
  | 'footer'
  | 'seo';

export const DEFAULT_SECTION_ORDER: LandingSectionName[] = [
  'hero', 'stats', 'features', 'howItWorks', 'pricing', 'testimonials', 'team', 'cta',
];

export const SECTION_LABELS: Record<LandingSectionName, string> = {
  hero: 'Hero',
  stats: 'Statistiques',
  features: 'Fonctionnalités',
  howItWorks: 'Comment ça marche',
  pricing: 'Tarifs',
  testimonials: 'Témoignages',
  team: 'Équipe',
  cta: 'Call to Action',
  footer: 'Pied de page',
  seo: 'SEO',
};

// ============================================================
// API FUNCTIONS
// ============================================================

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await safeJsonParse(response).catch(() => ({ message: 'Erreur serveur' }));
    throw new Error(
      (typeof error.error === 'object' ? error.error?.message : error.error) ||
      error.message ||
      `HTTP ${response.status}`
    );
  }
  const json = await safeJsonParse(response);
  return json.data;
}

async function fetchLandingCmsContent(): Promise<LandingPageContentData> {
  const response = await fetchWithAuth(`${API_BASE}/superadmin/landing-cms`);
  return handleResponse(response);
}

async function updateLandingCmsContent(data: Partial<LandingPageContentData>): Promise<LandingPageContentData> {
  const response = await fetchWithAuth(`${API_BASE}/superadmin/landing-cms`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return handleResponse(response);
}

async function updateLandingCmsSection(sectionName: string, data: any): Promise<LandingPageContentData> {
  const response = await fetchWithAuth(`${API_BASE}/superadmin/landing-cms/section/${sectionName}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return handleResponse(response);
}

async function publishLandingCms(): Promise<LandingPageContentData> {
  const response = await fetchWithAuth(`${API_BASE}/superadmin/landing-cms/publish`, {
    method: 'POST',
  });
  return handleResponse(response);
}

async function resetLandingCms(): Promise<LandingPageContentData> {
  const response = await fetchWithAuth(`${API_BASE}/superadmin/landing-cms/reset`, {
    method: 'POST',
  });
  return handleResponse(response);
}

async function uploadLandingImage(file: File): Promise<{ url: string; path: string; filename: string }> {
  const formData = new FormData();
  formData.append('image', file);

  const response = await fetchWithAuth(`${API_BASE}/superadmin/landing-cms/upload-image`, {
    method: 'POST',
    body: formData,
  });
  return handleResponse(response);
}

/** Public endpoint (no auth required) */
export async function fetchPublicLandingContent(): Promise<LandingPageContentData | null> {
  const response = await fetch(`${API_BASE}/public/landing-content`);
  if (!response.ok) return null;
  const json = await response.json();
  return json.data;
}

// ============================================================
// HOOKS
// ============================================================

const QUERY_KEY = ['landing-cms'];

/**
 * Get the landing CMS content (admin)
 */
export function useLandingCmsContent() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchLandingCmsContent,
  });
}

/**
 * Save full content (draft)
 */
export function useSaveLandingCms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateLandingCmsContent,
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEY, data);
    },
  });
}

/**
 * Update a single section
 */
export function useUpdateLandingSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionName, data }: { sectionName: string; data: any }) =>
      updateLandingCmsSection(sectionName, data),
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEY, data);
    },
  });
}

/**
 * Publish content
 */
export function usePublishLandingCms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: publishLandingCms,
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEY, data);
    },
  });
}

/**
 * Reset to defaults
 */
export function useResetLandingCms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resetLandingCms,
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEY, data);
    },
  });
}

/**
 * Upload image
 */
export function useUploadLandingImage() {
  return useMutation({
    mutationFn: uploadLandingImage,
  });
}
